export type ForecastResultPoint = {
  key: string;
  value: number;
  kind: "history" | "forecast";
};

export type ForecastResultModel = {
  narrative?: string;
  intent?: string;
  indexName?: string;
  timeField?: string;
  method?: string;
  direction?: string;
  nextExpected?: number;
  forecastMonth?: string;
  points: ForecastResultPoint[];
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function asNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pointFromRow(row: unknown, kind: "history" | "forecast"): ForecastResultPoint | null {
  if (!isRecord(row)) return null;
  const key =
    row.key != null
      ? String(row.key)
      : row.month != null
        ? String(row.month)
        : row.period != null
          ? String(row.period)
          : row.x != null
            ? String(row.x)
            : null;
  const value =
    asNumber(row.value) ??
    asNumber(row.orders) ??
    asNumber(row.count) ??
    asNumber(row.y) ??
    asNumber(row.estimatedOrders);
  if (!key || value == null) return null;
  return { key, value, kind };
}

function collectHistory(data: Record<string, unknown>): ForecastResultPoint[] {
  const candidates = [
    data.historicalMonthlyOrders,
    data.historicalOrders,
    data.history,
    data.series,
  ];
  for (const c of candidates) {
    if (!Array.isArray(c)) continue;
    const points = c
      .map((row) => pointFromRow(row, "history"))
      .filter((p): p is ForecastResultPoint => p != null);
    if (points.length > 0) return points;
  }
  return [];
}

function collectForecastPoints(
  data: Record<string, unknown>,
  methodResult: Record<string, unknown> | null
): { points: ForecastResultPoint[]; nextExpected?: number; forecastMonth?: string; method?: string; direction?: string } {
  const fromMethod = methodResult && Array.isArray(methodResult.forecast)
    ? methodResult.forecast
        .map((row) => pointFromRow(row, "forecast"))
        .filter((p): p is ForecastResultPoint => p != null)
    : [];
  if (fromMethod.length > 0) {
    return {
      points: fromMethod,
      nextExpected: asNumber(methodResult?.nextExpected) ?? fromMethod[0]?.value,
      method: methodResult?.method != null ? String(methodResult.method) : undefined,
      direction: methodResult?.direction != null ? String(methodResult.direction) : undefined,
    };
  }

  const forecastObj = isRecord(data.forecast) ? data.forecast : null;
  if (forecastObj) {
    const month =
      forecastObj.month != null
        ? String(forecastObj.month)
        : forecastObj.key != null
          ? String(forecastObj.key)
          : "forecast";
    const value =
      asNumber(forecastObj.estimatedOrders) ??
      asNumber(forecastObj.value) ??
      asNumber(forecastObj.orders) ??
      asNumber(forecastObj.nextExpected);
    if (value != null) {
      return {
        points: [{ key: month, value, kind: "forecast" }],
        nextExpected: value,
        forecastMonth: month,
        method: forecastObj.method != null ? String(forecastObj.method) : undefined,
      };
    }
  }

  if (Array.isArray(data.forecast)) {
    const points = data.forecast
      .map((row) => pointFromRow(row, "forecast"))
      .filter((p): p is ForecastResultPoint => p != null);
    if (points.length > 0) {
      return { points, nextExpected: points[0]?.value };
    }
  }

  const next = asNumber(data.nextExpected) ?? asNumber(methodResult?.nextExpected);
  if (next != null) {
    return {
      points: [{ key: "forecast", value: next, kind: "forecast" }],
      nextExpected: next,
      method: methodResult?.method != null ? String(methodResult.method) : undefined,
      direction: methodResult?.direction != null ? String(methodResult.direction) : undefined,
    };
  }

  return { points: [] };
}

/**
 * Detect freeform agent forecast/prediction JSON or analysisPayload-shaped objects.
 */
export function isForecastResultPayload(result: unknown): boolean {
  return parseForecastResult(result) != null;
}

export function parseForecastResult(result: unknown): ForecastResultModel | null {
  if (!isRecord(result)) return null;

  // Envelope: { response, data: {...} } or analysisPayload itself
  const narrative =
    typeof result.response === "string"
      ? result.response
      : typeof result.aiNarrative === "string"
        ? result.aiNarrative
        : typeof result.narrative === "string"
          ? result.narrative
          : undefined;

  const data = isRecord(result.data) ? result.data : result;
  const methodResult = isRecord(data.methodResult)
    ? data.methodResult
    : isRecord(result.methodResult)
      ? result.methodResult
      : null;

  const history = collectHistory(data);
  const forecast = collectForecastPoints(data, methodResult);
  const points = [...history, ...forecast.points];

  const looksLikeForecast =
    history.length > 0 && forecast.points.length > 0
    || (history.length > 0 && forecast.nextExpected != null)
    || (methodResult != null && (Array.isArray(methodResult.forecast) || methodResult.nextExpected != null) && history.length > 0)
    || (isRecord(data.forecast) && (data.forecast.estimatedOrders != null || data.forecast.value != null) && history.length > 0);

  if (!looksLikeForecast || points.length === 0) {
    return null;
  }

  const indexName =
    data.index != null
      ? String(data.index)
      : data.indexName != null
        ? String(data.indexName)
        : result.indexName != null
          ? String(result.indexName)
          : undefined;
  const timeField =
    data.dateField != null
      ? String(data.dateField)
      : data.timeField != null
        ? String(data.timeField)
        : result.timeField != null
          ? String(result.timeField)
          : undefined;
  const method =
    forecast.method
    || (data.method != null ? String(data.method) : undefined)
    || (result.method != null ? String(result.method) : undefined)
    || (methodResult?.method != null ? String(methodResult.method) : undefined);
  const intent =
    data.intent != null
      ? String(data.intent)
      : result.intent != null
        ? String(result.intent)
        : undefined;

  return {
    narrative,
    intent,
    indexName,
    timeField,
    method,
    direction: forecast.direction,
    nextExpected: forecast.nextExpected,
    forecastMonth: forecast.forecastMonth,
    points,
  };
}

export function buildForecastChartOption(
  model: ForecastResultModel,
  labels: { history: string; forecast: string; value: string }
): Record<string, unknown> {
  const categories = model.points.map((p) => p.key);
  const historyData = model.points.map((p) => (p.kind === "history" ? p.value : null));
  const forecastData = model.points.map((p) => (p.kind === "forecast" ? p.value : null));

  return {
    chartType: "bar",
    tooltip: { trigger: "axis" },
    legend: { data: [labels.history, labels.forecast] },
    grid: { left: 48, right: 24, top: 40, bottom: 48 },
    xAxis: { type: "category", data: categories, axisLabel: { rotate: categories.length > 6 ? 30 : 0 } },
    yAxis: { type: "value", name: labels.value },
    series: [
      {
        name: labels.history,
        type: "bar",
        data: historyData,
        itemStyle: { color: "#5B8FF9" },
      },
      {
        name: labels.forecast,
        type: "bar",
        data: forecastData,
        itemStyle: { color: "#F6BD16", borderType: "dashed" },
      },
    ],
  };
}
